
import React, { useState, useEffect, useCallback } from 'react';
import { useClubCoaches } from '@/hooks/useClubCoaches';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, Edit, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { ImageUpload } from '@/components/ui/image-upload';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { ClubCoach } from '@/hooks/useClubCoaches';

interface ClubCoachManagementProps {
  clubId: string;
  clubName: string;
}

export const ClubCoachManagement = ({ clubId, clubName }: ClubCoachManagementProps) => {
  const {
    clubCoaches,
    availableCoaches,
    loading,
    removing,
    fetchClubCoaches,
    addCoachToClub,
    removeCoachFromClub,
    updateClubCoach
  } = useClubCoaches();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCoachId, setSelectedCoachId] = useState('');
  const [editingCoach, setEditingCoach] = useState<ClubCoach | null>(null);
  
  // Form fields for editing
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [bio, setBio] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState('');

  useEffect(() => {
    if (clubId) {
      fetchClubCoaches(clubId);
    }
  }, [clubId, fetchClubCoaches]);

  const handleAddCoach = async () => {
    if (!selectedCoachId) {
      toast.error('Please select a coach to add');
      return;
    }

    try {
      await addCoachToClub(clubId, selectedCoachId);
      setIsDialogOpen(false);
      setSelectedCoachId('');
    } catch (error) {
      console.error('Error adding coach:', error);
    }
  };

  const handleEditCoach = (clubCoach: ClubCoach) => {
    setEditingCoach(clubCoach);
    setTitle(clubCoach.title || '');
    setPhone(clubCoach.phone || '');
    setEmail(clubCoach.email || '');
    setBio(clubCoach.bio || '');
    setProfilePictureUrl(clubCoach.profile_picture_url || '');
    setIsEditDialogOpen(true);
  };

  const handleUpdateCoach = async () => {
    if (!editingCoach) return;

    try {
      await updateClubCoach(editingCoach.id, {
        title,
        phone,
        email,
        bio,
        profile_picture_url: profilePictureUrl
      });
      
      setIsEditDialogOpen(false);
      resetEditForm();
    } catch (error) {
      console.error('Error updating coach:', error);
    }
  };

  const resetEditForm = () => {
    setEditingCoach(null);
    setTitle('');
    setPhone('');
    setEmail('');
    setBio('');
    setProfilePictureUrl('');
  };

  const handleRemoveCoach = async (clubCoachId: string) => {
    try {
      await removeCoachFromClub(clubCoachId, clubId);
    } catch (error) {
      console.error('Error in remove coach handler:', error);
      toast.error('Failed to remove coach. Please try again.');
    }
  };

  // Filter out coaches already assigned to this club
  const unassignedCoaches = availableCoaches.filter(
    coach => !clubCoaches.some(cc => cc.coach_id === coach.id)
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-md font-medium">Coaches for {clubName}</h4>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Coach
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Coach to {clubName}</DialogTitle>
              <DialogDescription>
                Select a coach to assign to this club.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Select value={selectedCoachId} onValueChange={setSelectedCoachId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a coach" />
                </SelectTrigger>
                <SelectContent>
                  {unassignedCoaches.length === 0 ? (
                    <SelectItem value="" disabled>
                      No available coaches
                    </SelectItem>
                  ) : (
                    unassignedCoaches.map((coach) => (
                      <SelectItem key={coach.id} value={coach.id}>
                        {coach.first_name} {coach.last_name} - {coach.email}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddCoach} disabled={!selectedCoachId}>
                Add Coach
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Coach Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Coach Information</DialogTitle>
            <DialogDescription>
              Update the coach's title and contact information for this club.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <ImageUpload
              currentImageUrl={profilePictureUrl}
              onImageUploaded={setProfilePictureUrl}
              onImageRemoved={() => setProfilePictureUrl('')}
              label="Coach Profile Picture"
            />
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Head Coach, Assistant Coach"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g., (555) 123-4567"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Contact Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="coach@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Coach's background, experience, achievements..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateCoach}>
              Update Coach
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading coaches...</span>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Coach</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Added</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clubCoaches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No coaches assigned to this club
                </TableCell>
              </TableRow>
            ) : (
              clubCoaches.map((clubCoach) => (
                <TableRow key={clubCoach.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={clubCoach.profile_picture_url} />
                        <AvatarFallback>
                          {clubCoach.profiles.first_name[0]}{clubCoach.profiles.last_name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex items-center gap-2">
                        <span>{clubCoach.profiles.first_name} {clubCoach.profiles.last_name}</span>
                        <button
                          onClick={() => window.open(`/coach/${clubCoach.coach_id}`, '_blank')}
                          className="text-blue-500 hover:text-blue-700"
                          title="View coach profile"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{clubCoach.email || clubCoach.profiles.email}</TableCell>
                  <TableCell>{clubCoach.title || '-'}</TableCell>
                  <TableCell>{clubCoach.phone || '-'}</TableCell>
                  <TableCell>{new Date(clubCoach.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditCoach(clubCoach)}
                        disabled={removing === clubCoach.id}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={removing === clubCoach.id}
                            className={removing === clubCoach.id ? "opacity-50" : ""}
                          >
                            {removing === clubCoach.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2">
                              <AlertTriangle className="h-5 w-5 text-amber-500" />
                              Remove Coach from Club
                            </AlertDialogTitle>
                            <AlertDialogDescription className="space-y-2">
                              <p>
                                Are you sure you want to remove <strong>{clubCoach.profiles.first_name} {clubCoach.profiles.last_name}</strong> from <strong>{clubName}</strong>?
                              </p>
                              <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                                <p className="text-sm text-amber-800 font-medium mb-1">This action will:</p>
                                <ul className="text-sm text-amber-700 space-y-1">
                                  <li>• Remove them from the club's coach list</li>
                                  <li>• Remove them from any teams they coach in this club</li>
                                  <li>• This action cannot be undone</li>
                                </ul>
                              </div>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRemoveCoach(clubCoach.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Remove Coach
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
